import { Card, CardHeader, ErrorState, Icon, Loading, Pill } from '../components/ui';
import { useApi } from '../lib/useApi';
import './shared.css';

interface IntegrationStatus {
  key: string;
  name: string;
  configured: boolean;
  detail: string;
  hint?: string;
}

/**
 * Whether the things the portal depends on are actually working.
 *
 * Read-only on purpose. All of it is set in the environment the API boots
 * with, and a form here would imply it could be changed from a browser — it
 * cannot, and the process only reads its configuration once at startup. What
 * this answers is the question that previously meant reading a log: is mail
 * going out, and as whom.
 */
export function IntegrationsPanel() {
  const { data, error, loading, reload } = useApi<IntegrationStatus[]>('/settings/integrations');

  if (loading) return <Loading label="Checking integrations…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Card>
      <CardHeader
        title="Integrations"
        meta="Set in the API's environment, not here. Changing one needs a restart."
      />
      <div className="stack" style={{ gap: 12 }}>
        {(data ?? []).map((item) => (
          <div key={item.key} className="person">
            <div className="person__body">
              <div className="person__head">
                <strong>{item.name}</strong>
                <Pill tone={item.configured ? 'ok' : 'warn'}>
                  {item.configured ? 'Configured' : 'Not configured'}
                </Pill>
              </div>
              <div className="person__meta">{item.detail}</div>
              {item.hint && (
                <div className="notice" style={{ marginTop: 8 }}>
                  {Icon.alert({ size: 15 })}
                  <span>{item.hint}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

interface SecurityPolicy {
  password: { minLength: number; breachCheck: boolean };
  session: { accessTtl: string; refreshTtl: string; idleTimeoutMinutes: number };
  lockout: { maxLoginAttempts: number; lockoutMinutes: number };
  links: { inviteTtlHours: number; passwordResetTtlMinutes: number; appUrl: string };
  mfa: {
    issuer: string;
    codeTtlSeconds: number;
    maxAttempts: number;
    totpWindow: number;
    recoveryCodeCount: number;
  };
}

function Rule({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', gap: 16 }}>
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** The rules sign-in enforces, stated where someone can check them. */
export function SecurityPanel() {
  const { data, error, loading, reload } = useApi<SecurityPolicy>('/settings/security');

  if (loading) return <Loading label="Reading the security policy…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;

  const minutes = (n: number) => (n === 1 ? '1 minute' : `${n} minutes`);

  return (
    <div className="stack">
      <Card>
        <CardHeader title="Passwords" />
        <div className="stack" style={{ gap: 9 }}>
          <Rule label="Minimum length" value={`${data.password.minLength} characters`} />
          <Rule
            label="Checked against known breaches"
            value={data.password.breachCheck ? 'Yes' : 'No'}
          />
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Length is the only composition rule, following NIST 800-63B. A long
            passphrase beats a short password with a symbol bolted on, and
            forced complexity produces predictable substitutions.
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader title="Sessions" />
        <div className="stack" style={{ gap: 9 }}>
          <Rule label="Access token lifetime" value={data.session.accessTtl} />
          <Rule label="Sign-in valid for" value={data.session.refreshTtl} />
          <Rule label="Signed out after idle" value={minutes(data.session.idleTimeoutMinutes)} />
          <Rule
            label="Locked after"
            value={`${data.lockout.maxLoginAttempts} failed attempts, for ${minutes(
              data.lockout.lockoutMinutes,
            )}`}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Second factor" />
        <div className="stack" style={{ gap: 9 }}>
          <Rule label="Shown in authenticator apps as" value={data.mfa.issuer} />
          <Rule
            label="Texted and emailed codes last"
            value={minutes(Math.round(data.mfa.codeTtlSeconds / 60))}
          />
          <Rule label="Attempts per code" value={String(data.mfa.maxAttempts)} />
          <Rule
            label="Clock drift allowed"
            value={`±${data.mfa.totpWindow * 30} seconds`}
          />
          <Rule label="Recovery codes issued" value={String(data.mfa.recoveryCodeCount)} />
        </div>
      </Card>

      <Card>
        <CardHeader title="Links sent by email" />
        <div className="stack" style={{ gap: 9 }}>
          <Rule label="Invitations expire after" value={`${data.links.inviteTtlHours} hours`} />
          <Rule
            label="Password reset links expire after"
            value={minutes(data.links.passwordResetTtlMinutes)}
          />
          <Rule label="Links are built from" value={data.links.appUrl} />
          {data.links.appUrl.startsWith('http://') && (
            <div className="notice notice--danger" style={{ marginTop: 6 }}>
              {Icon.alert({ size: 15 })}
              <span>
                That address is plain HTTP, so it only works on this machine.
                Anyone opening an invitation elsewhere will not reach the portal.
              </span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
